import { ref, computed, watch } from '@vue/composition-api'
// import { title } from '@core/utils/filter'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import router from '@/router'
import ability from '@/libs/acl/ability'
import axios from '@/libs/axios'
import { debounce } from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'
import store from "@/store";

export default function useList() {
    const refListTable = ref(null)
    const toast = useToast()
    store.dispatch("maintenance_template/getAll");
    store.dispatch("service_orders/getAll");
    store.dispatch("service_orders_status/getAll");
    store.dispatch("unit/getAll");
    const templateOptions = computed(() => store.getters["maintenance_template/getSelectedItems"]);
    const serviceOrdersStatusOptions = computed(() => store.getters["service_orders_status/getSelectedItems"]);
    const unitOptions = computed(() => store.getters["unit/getSelectedItems"]);
    const serviceOrdersStatusFilter = ref(null);
    const templateFilter = ref(null);
    const serviceOrderFilter = ref(null);
    const unitFilter = ref(null);

    const listFilter = ref(null)
    if (router.currentRoute.params.type)
        listFilter.value = parseInt(router.currentRoute.type)

    if (router.currentRoute.query.id_service_order) {
        serviceOrderFilter.value = [parseInt(router.currentRoute.query.id_service_order)];
    }
    const tableColumns = [
        {
            label: 'actions.name',
            key: 'actions'
        },
        {
            label: 'unit.single',
            key: 'unit',
            formatter: 'formatUnit'
        },
        {
            label: 'maintenance.form_name',
            key: 'name',
            sortable: true
        },
        {
            label: 'maintenance.template_name',
            key: 'template.name'
        },
        {
            label: 'maintenance.serviceOrder',
            key: 'service_order.reference_number',
            sortable: true
        },
        { label: 'maintenance.status.pending', key: 'pending', },
        { label: 'maintenance.status.in_progress', key: 'in_progress' },
        { label: 'maintenance.status.in_review', key: 'in_review' },
        { label: 'maintenance.status.finished', key: 'finished' },
        { label: 'maintenance.status.canceled', key: 'canceled' },
        {
            label: 'date.created_at',
            key: 'created_at',
            sortable: true,
            formatter: 'formatDateAssigned'
        },
        {
            label: 'date.updated_at',
            key: 'updated_at',
            sortable: true,
            formatter: 'formatDateAssigned'
        },
    ];

    const perPage = ref(10);
    const totalList = ref(0);
    const currentPage = ref(1);
    const perPageOptions = [10, 25, 50, 100];
    const searchQuery = ref('');
    const sortBy = ref('id');
    const isSortDirDesc = ref(true);
    const safrMasterAccountId = ref(null);
    const action = ref('');

    const dataMeta = computed(() => {
        const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
        return {
            from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: totalList.value,
        }
    });

    const fetchList = async () => {
        let params = {
            q: searchQuery.value,
            max: perPage.value,
            'page[n]': currentPage.value,
            sortBy: sortBy.value,
            sortDesc: isSortDirDesc.value,
            type: listFilter.value,
            id_template: templateFilter.value,
            id_service_order: serviceOrderFilter.value,
            status: serviceOrdersStatusFilter.value,
            id_unit: unitFilter.value,
            action: action.value,
        }
        try {
            const response = await axios.get('/v1/maintenance-forms', {
                params,
                responseType: params.action === 'export' ? 'blob' : 'json',
            });

            if (!response.data) return [];
            if (params.action === 'export') {
                const url = window.URL.createObjectURL(
                    new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    })
                );
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute(
                    "download",
                    response.headers["content-disposition"].split("filename=")[1]
                );
                document.body.appendChild(link);
                link.click();
                link.remove();
                const notification = {
                    id: response.headers["content-disposition"].split("filename=")[1],
                    progress: 100,
                    completed: true,
                    view: "directory",
                    url: url,
                    download: response.headers["content-disposition"].split("filename=")[1],
                    title: "Reporte de Formularios",
                    body: "Descarga completa",
                    error: false,
                    timestamp:
                    new Date().toLocaleDateString() + " " + new Date().toLocaleTimeString(),
                };
                store.dispatch("grid/updateDownloadNotification", notification);
                return [];
            }

            const { data: dataApi, meta, success } = response.data;
            if (!success) {
                contentToast('Error obteniendo la información', 'danger');
                return [];
            }

            totalList.value = meta.total;
            return dataApi;

        } catch (error) {
            contentToast('Error en la petición', 'danger');
            console.error(error);
            return [];
        }
    }

    const formatDateAssigned = value => {
        const formattedDate = moment(value).format('DD/MM/YYYY HH:mm:ss');
        return formattedDate;
    }

    const formatUnit = (value, key, item) => {
        if (item.service_order && item.service_order.unit) {
            const unit = item.service_order.unit;
            return unit.description || '-';
        }
        return '-';
    }

    const refetchData = () => {
        refListTable.value.refresh();
    }

    const contentToast = (message, type) => toast({
        component: ToastificationContent,
        props: {
            title: message,
            icon: 'CheckIcon',
            variant: type
        }
    });

    watch([listFilter, currentPage, perPage], () => {
        refetchData();
    });

    watch([searchQuery], debounce(() => {
        refetchData()
    }, 500))

    const filters = ref({});
    const filtersCount = useFilterCounter(filters);
    const buildFilters = () => {
        filters.value = {
            id_template: templateFilter.value,
            id_service_order: serviceOrderFilter.value,
            status: serviceOrdersStatusFilter.value,
            id_unit: unitFilter.value,
        };
        refetchData();
    }
    const clearSearchFilters = () => {
        templateFilter.value = null;
        serviceOrderFilter.value = null;
        serviceOrdersStatusFilter.value = null;
        unitFilter.value = null;
        buildFilters();
    }

    return {
        tableColumns,
        perPage,
        currentPage,
        totalList,
        dataMeta,
        perPageOptions,
        searchQuery,
        sortBy,
        isSortDirDesc,
        refListTable,
        safrMasterAccountId,
        listFilter,
        filtersCount,
        templateOptions,
        templateFilter,
        serviceOrderFilter,
        serviceOrdersStatusOptions,
        serviceOrdersStatusFilter,
        unitOptions,
        unitFilter,
        action,

        fetchList,
        refetchData,
        contentToast,
        clearSearchFilters,
        buildFilters,
        formatDateAssigned,
        formatUnit,
    }
}
